import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, CheckCircle, AlertCircle, Loader2, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';

type State =
  | { phase: 'loading' }
  | { phase: 'success'; email: string | null }
  | { phase: 'taken'; message: string }
  | { phase: 'error'; message: string };

/**
 * ConfirmEmailChange (2026-09-15) — public, token-bound confirmation page for
 * the post-claim email verification of visitor-invite claimants.
 *
 * The claim flow emails a link to /confirm-email-change?token=... — the
 * visitor typically has NO session (claim-created account), so this page calls
 * the PUBLIC token-bound endpoint POST /api/visitor-invites/confirm-email,
 * which resolves the account from the token row itself and — when the address
 * is free — backfills users.email + email_verified=1 ("email IS the user").
 * If the address belongs to another account, the backend answers 409
 * EMAIL_TAKEN and we show its clear message (log in with that account).
 */
export default function ConfirmEmailChange() {
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();
  const [state, setState] = useState<State>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token || token.length < 32) {
        if (!cancelled) setState({ phase: 'error', message: 'El enlace no es válido. Solicita un nuevo correo de verificación.' });
        return;
      }
      try {
        const res = await axios.post(`${API_URL}/visitor-invites/confirm-email`, { token });
        if (cancelled) return;
        const data = res.data?.data ?? {};
        setState({ phase: 'success', email: data?.email ?? null });
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.response?.status;
        const msg: string = err?.response?.data?.error || 'No pudimos confirmar tu correo. Intenta de nuevo.';
        if (status === 409 && err?.response?.data?.code === 'EMAIL_TAKEN') setState({ phase: 'taken', message: msg });
        else setState({ phase: 'error', message: msg });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-full bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 bg-[#36CCD8] rounded-xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Confirmar tu correo</h1>

        {state.phase === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-6">
            <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
            <p className="text-sm text-slate-500">Confirmando tu correo…</p>
          </div>
        )}

        {state.phase === 'success' && (
          <div className="py-4">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm text-slate-600">
              Tu correo{state.email ? <span className="font-medium"> {state.email}</span> : null} quedó confirmado y
              vinculado a tu cuenta. Ya puedes entrar con tu teléfono o tu correo y tu contraseña.
            </p>
            <Link
              to="/login"
              className="mt-5 inline-flex items-center justify-center w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl text-base font-semibold"
            >
              Entrar a la app
            </Link>
          </div>
        )}

        {state.phase === 'taken' && (
          <div className="py-4">
            <Mail className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg text-sm">
              {state.message}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Si ese correo es tuyo, entra directamente con él y tu contraseña. Si no, puedes reclamar tu invitación
              con otro correo.
            </p>
            <Link
              to="/login"
              className="mt-5 inline-flex items-center justify-center w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl text-base font-semibold"
            >
              Ir a iniciar sesión
            </Link>
          </div>
        )}

        {state.phase === 'error' && (
          <div className="py-4">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
              {state.message}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              El enlace expira en 24 horas y es de un solo uso. Si el problema persiste, reclama tu invitación de
              nuevo para recibir otro correo.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
